<template>
    <div class="transparent no-shadows elevation-0">
        <div>
            <div class="d-flex flex-column">
                <v-row class="mt-3">
                    <v-col cols="12" md="6" class="grid-title">
                        <h3 v-if="gridTitle" class="m-0" v-html="gridTitle"></h3>
                        <slot name="subtitle"></slot>
                    </v-col>
                    <v-col cols="12" md="6" class="d-flex justify-content-end py-2">
                        <div v-if="!hideButtons">
                            <slot name="buttons"></slot>
                        </div>
                        <div v-if="filtersWrap" class="px-2">
                            <v-tooltip top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        :color="showFilters ? '#b3b3b3' : 'primary'"
                                        dark
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="showFilters = true"
                                    >
                                        <v-icon>mdi-filter</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.filters') }}</span>
                            </v-tooltip>
                        </div>
                        <div
                            v-if="!!hasFilterSelector && !!endpoint && !!$refs.filterSelector && !hidePageConfigurator"
                            class="px-2"
                        >
                            <v-tooltip top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="primary"
                                        dark
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="$refs.filterSelector.saveDialog = true"
                                    >
                                        <v-icon>mdi-tune</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('filters::button.page.config') }}</span>
                            </v-tooltip>
                        </div>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col v-if="!!hasFilterSelector && !!endpoint" class="filters-selector">
                        <b-datatable-filter-selector
                            :key="filterSelectorKey"
                            ref="filterSelector"
                            :hidden-columns="hidden"
                            :route="endpoint"
                            :table-columns="tableColumns"
                            @change="updateColumns"
                            @create="createFilter"
                            @filterChange="setFilter"
                            @reset="resetTable"
                        ></b-datatable-filter-selector>
                    </v-col>
                </v-row>
            </div>

            <div v-if="showFilters" class="filters filters-form w-100 py-2">
                <v-slide-y-transition>
                    <v-row v-show="showFilters" class="filters-fields m-0">
                        <div class="px-1 w-100">
                            <v-col cols="12" class="d-flex align-center justify-content-between pb-0">
                                <div class="d-flex align-center">
                                    <v-icon left>mdi-filter</v-icon>
                                    <h6 class="mb-0">{{ t('filters::title.filter') }}</h6>
                                </div>
                                <div>
                                    <v-tooltip top transition="v-fab-transition">
                                        <template #activator="{ on, attrs }">
                                            <v-btn
                                                color="primary"
                                                icon
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="showFilters = false"
                                            >
                                                <v-icon>mdi-close</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>{{ t('default::button.close') }}</span>
                                    </v-tooltip>
                                </div>
                            </v-col>
                            <v-col cols="12" class="pt-0 pb-6">
                                <v-row class="m-0">
                                    <v-row>
                                        <slot name="filters" :filters="{ filters, applyFilters }"></slot>
                                    </v-row>
                                </v-row>
                            </v-col>
                            <v-col cols="12" class="filters-actions d-flex justify-content-between">
                                <div class="flex-grow-1">
                                    <i v-if="options.page > 1"
                                        >You are currently viewing page {{ options.page }} of the list</i
                                    >
                                </div>
                                <div class="flex-grow-0">
                                    <v-btn text rounded color="primary" class="mr-4" @click="resetTable()">
                                        {{ t('filters::button.reset') }}
                                    </v-btn>
                                    <v-btn :loading="loading" rounded color="primary" @click="applyFilters(true)">
                                        {{ t('filters::button.filter') }}
                                    </v-btn>
                                </div>
                            </v-col>
                        </div>
                    </v-row>
                </v-slide-y-transition>
            </div>
            <b-alert
                v-if="showInfoBox"
                type="info"
                message="Currently viewing a paginated/filtered page - picking up right where you left off last time."
            ></b-alert>
            <v-data-table
                ref="vdatatable"
                :key="datatableInternalKey"
                v-model="selected"
                :footer-props="footerProps"
                :headers="filteredColumns"
                :items="items"
                v-bind="$attrs"
                :items-per-page="perPage"
                :loading="loading"
                :loading-text="t('datatable::label.loading.text')"
                :multi-sort="isPreviewMode ? false : multiSort"
                :no-data-text="t('datatable::no.data.text')"
                :options.sync="options"
                :server-items-length="totalItems"
                :show-select="!!hasBulkOperations"
                :class="`${!!hasBulkOperations ? 'bulk-mode' : null} ${disabledSelect ? 'disabled-select' : null}`"
                v-on="$listeners"
            >
                <template #[`body.prepend`]>
                    <tr
                        v-if="hasBulkOperations || isPreviewMode"
                        :class="`bulk-actions ${selected.length !== 0 || isPreviewMode ? 'opened' : ''}`"
                    >
                        <v-fade-transition>
                            <v-col
                                v-if="selected.length !== 0 || isPreviewMode"
                                class="bulk-actions-wrap d-flex"
                                cols="12"
                            >
                                <div class="d-flex align-center">
                                    <v-btn
                                        icon
                                        class="ml-4 close-button"
                                        :disabled="disabledSelect"
                                        small
                                        @click="selected = []"
                                    >
                                        <v-icon>mdi-close</v-icon>
                                    </v-btn>
                                    <div class="selected-count">
                                        <span class="count">
                                            {{ selected.length }}
                                        </span>
                                        <span class="label">
                                            {{ t('default::grid.label.selected') }}
                                        </span>
                                    </div>
                                    <slot
                                        name="bulkOperations"
                                        :bulkOperations="{ bulkOperations, applyFilters }"
                                    ></slot>
                                </div>
                            </v-col>
                        </v-fade-transition>
                    </tr>
                </template>

                <template v-if="hasScreenConfigurator && filtersWrap" #[`header.actions`]>
                    <div
                        v-if="filteredColumns && !!$refs.filterSelector && items.length"
                        class="v-data-table-header-column-s"
                    >
                        <v-tooltip top transition="v-fab-transition">
                            <template #activator="{ on, attrs }">
                                <v-btn
                                    color="rgba(0,0,0,0.56)"
                                    dark
                                    icon
                                    v-bind="attrs"
                                    v-on="on"
                                    @click="openPageConfigDialog"
                                >
                                    <v-icon>mdi-cog</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ t('filters::button.select.visible.columns') }}</span>
                        </v-tooltip>
                    </div>
                    <div v-else>{{ t('default::grid.label.actions') }}</div>
                </template>

                <template #[`footer.page-text`]="{ pageStart, pageStop, itemsLength }">
                    {{ pageStart }} - {{ pageStop }} {{ t('datatable::pagination.of') }} {{ itemsLength }}
                </template>

                <template v-for="(_, slot) of $scopedSlots" #[slot]="scope">
                    <slot :name="slot" v-bind="scope" :meta-data="metaData" :td-totals-class="tdTotalsClass"></slot>
                </template>
            </v-data-table>
        </div>
    </div>
</template>

<script>
import BAlert from './BAlert.vue';

export default {
    name: 'DataTable',
    components: { BAlert },
    props: {
        datatableKey: {
            type: Number,
            required: false,
            default: 0,
        },
        gridTitle: {
            type: String,
            required: false,
            default: null,
        },
        endpoint: {
            type: String,
            required: false,
            default: null,
        },
        filtersEndpoint: {
            type: String,
            required: false,
            default: null,
        },
        perPage: {
            type: Number,
            required: false,
            default: 30,
        },
        columns: {
            type: Array,
            required: true,
        },
        hiddenColumns: {
            type: Array,
            required: false,
            default: function () {
                return [];
            },
        },
        withDeleted: {
            type: Boolean,
            required: false,
            default: false,
        },
        multiSort: {
            type: Boolean,
            required: false,
            default: true,
        },
        hasFilterSelector: {
            type: Boolean,
            required: false,
            default: false,
        },
        hasFilters: {
            type: Boolean,
            required: false,
            default: true,
        },
        hasBulkOperations: {
            type: Boolean,
            required: false,
            default: false,
        },
        hasScreenConfigurator: {
            type: Boolean,
            required: false,
            default: true,
        },
        metaFields: {
            type: Array,
            required: false,
            default: function () {
                return [];
            },
        },
        initialFilters: {
            type: Object,
            required: false,
            default: function () {
                return {};
            },
        },
        initialSort: {
            type: String,
            required: false,
            default: null,
        },
        hideButtons: {
            type: Boolean,
            required: false,
            default: false,
        },
        hidePageConfigurator: {
            type: Boolean,
            required: false,
            default: false,
        },
        filtersExpanded: {
            type: Boolean,
            required: false,
            default: false,
        },
        selectedItems: {
            type: Array,
            required: false,
            default: function () {
                return [];
            },
        },
        disabledSelect: {
            type: Boolean,
            required: false,
            default: false,
        },
        previewMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        notification: {
            type: Boolean,
            required: false,
            default: false,
        },
        qcMode: {
            type: Boolean,
            required: false,
            default: false,
        },
        fakeScroller: {
            type: Boolean,
            required: false,
            default: true,
        },
        filterExpanded: {
            type: Boolean,
            required: false,
            default: false,
        },
        keepFilters: {
            type: Boolean,
            required: false,
            default: true,
        },
        keepFiltersPrefix: {
            type: String,
            required: false,
            default: 'datatable',
        },
    },
    data() {
        if (this.keepFilters) {
            let key = this.keepFiltersPrefix + '-' + this.endpoint;
            let data = JSON.parse(window.localStorage.getItem(key));
            if (_.isObject(data)) {
                data.showFilters = !_.isEqual(data.filters, this.initialFilters);
                return data;
            }
        }

        return this.initialState();
    },
    computed: {
        showInfoBox() {
            return this.showFilters === false && this.options.page > 1;
        },
        currentTab() {
            return this.$store.state.data_table.tab;
        },
        filteredColumns() {
            return this.headers.filter((column) => {
                if (this.previewMode) {
                    column.sortable = false;
                }
                return !this.hidden.includes(column.value);
            });
        },
        tdTotalsClass() {
            return {
                'v-data-table__mobile-row': this.windowWidth < 600,
            };
        },
        fields() {
            return this.tableColumns
                .filter((column) => {
                    return column.fields === undefined || column.fields !== null;
                })
                .map((column) => column.fields || column.value);
        },
        sort() {
            let initialSort = this.initialSort;

            let sortData = this.options.sortBy.map((value, key) => {
                if (initialSort && value === initialSort.replace(/^-/, '')) {
                    initialSort = null;
                }

                return (this.options.sortDesc[key] ? '' : '-') + value;
            });

            if (initialSort) {
                sortData.push(initialSort);
            }

            return sortData;
        },
    },
    watch: {
        filters: {
            handler: _.debounce(function (filters) {
                Object.keys(filters).forEach((filter) => {
                    if (_.isArray(filters[filter])) {
                        {
                            const hasNullValue = filters[filter].includes(null);
                            if (hasNullValue) {
                                filters[filter] = null;
                            }
                        }
                    }
                });
            }),
            deep: true,
        },
        'filters.filter_log_date': {
            handler: _.debounce(function (val) {
                if (val == null) return;
                return _.set(this.filters, 'log_date', this.dateFilterConverter(val));
            }),
        },
        'filters.filter_date_taken': {
            handler: _.debounce(function (val) {
                if (val == null) return;
                return _.set(this.filters, 'date_taken', this.dateFilterConverter(val));
            }),
        },
        'filters.filter_created_at': {
            handler: _.debounce(function (val) {
                if (val == null) return;
                return _.set(this.filters, 'created_at', this.dateFilterConverter(val));
            }),
        },
        'filters.filter_expires_on': {
            handler: _.debounce(function (val) {
                if (val == null) return;
                return _.set(this.filters, 'expires_on', this.dateFilterConverter(val));
            }),
        },
        'filters.filter_group_date': {
            handler: _.debounce(function (val) {
                if (val == null) return;
                return _.set(this.filters, 'group_date', this.dateFilterConverter(val));
            }),
        },
        'filters.filter_event_date': {
            handler: _.debounce(function (val) {
                if (val == null) return;
                return _.set(this.filters, 'event_date', this.dateFilterConverter(val));
            }),
        },
        options: {
            handler: _.debounce(function () {
                this.loading = false;
                this.loadItems().then((data) => {
                    this.headers = data.columns;
                    this.items = data.items;
                    this.totalItems = data.total;
                });
            }),
            deep: true,
        },
        selected(val) {
            if (this.isPreviewMode && val.length === 0) {
                this.selected = val;
                this.isPreviewMode = false;
            }
            this.$emit('update-selected-items', val);
        },
        showFilters(val) {
            return val
                ? window.addEventListener('keypress', this.search)
                : window.removeEventListener('keypress', this.search);
        },
        previewMode(val) {
            this.setPreviewMode(val);
        },
        items() {
            if (this.qcMode) {
                _.delay(() => {
                    this.setQcMode();
                }, 300);
            }
            if (this.fakeScroller) {
                _.delay(() => {
                    this.initScroller();
                }, 300);
            }
        },
    },
    mounted() {
        //When explicitly set to true outside this component, show filters.
        // If not, leave internal logic to manage it.
        if (this.filtersExpanded === true) {
            this.showFilters = true;
        }
        _.delay(() => this.tableMounted(), 600);
    },
    created() {},
    beforeDestroy() {
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('keypress', this.search);
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('scroll', this.onTableScroll);
    },
    methods: {
        isFilterExpanded() {
            if (this.filtersExpanded === true) {
                this.showFilters = true;
            }
        },
        tableMounted() {
            this.$nextTick(() => {
                window.addEventListener('resize', this.onResize);
                window.addEventListener('scroll', this.onScroll);
                window.addEventListener('scroll', this.onTableScroll);
            });
            this.onResize();
            this.notification ? this.notificationMode() : false;
            if (this.filtersExpanded) {
                window.addEventListener('keypress', this.search);
            }
            this.$store.dispatch('data_table/setHiddenColumns', this.hiddenColumns);
        },
        initScroller() {
            const element = this.$el.querySelector('table');
            let tableHeader = this.$el.querySelector('thead.v-data-table-header');
            const cloneHeader = tableHeader.cloneNode(true);
            cloneHeader.classList.remove('v-data-table-header');
            cloneHeader.classList.add('v-data-table-fake-header');
            cloneHeader.style = new Object();
            if (!this.$el.querySelector('thead.v-data-table-fake-header')) {
                element.appendChild(cloneHeader);
            }
            // simulate scroll
            _.delay(
                () => {
                    window.dispatchEvent(new Event('scroll'));
                },
                1000,
                'latter'
            );
        },
        setQcMode() {
            this.$el;
            if (!this.items.length) {
                return;
            }
            this.items.forEach((item, index) => {
                const currentRow = this.$el.querySelector('tbody').childNodes[index] || false;
                if (!currentRow) return;
                if (item.visible_in_qc_list && !item.is_patient_mode) {
                    currentRow.classList.add(...['qc-mode', 'color-red']);
                }
                if (item.rejected) {
                    currentRow.classList.add(...['qc-mode', 'color-red']);
                }

                if (item.error_message) {
                    currentRow.classList.add(...['qc-mode', 'color-red']);
                }
            });
        },
        switchTab() {
            this.$store.dispatch('data_table/setHiddenColumns', this.hidden);
            this.$store.dispatch('data_table/setColumns', this.columns);
            this.filterSelectorKey++;
        },
        dateFilterConverter(val) {
            let dates = _.clone(val);
            if (!val) {
                return null;
            }
            if (val && val.length <= 1) {
                return `${val[0]}`;
            }
            if (moment(val[0]).isAfter(moment(val[1]))) {
                dates[0] = `gte:${val[1]}`;
                dates[1] = `lte:${val[0]}`;
            }
            if (moment(val[1]).isAfter(moment(val[0]))) {
                dates[0] = `gte:${val[0]}`;
                dates[1] = `lte:${val[1]}`;
            }
            return dates.join(',');
        },
        notificationMode() {
            if (!this.items.length) {
                return;
            }
            this.items.forEach((item, index) => {
                const currentRow = this.$el.querySelector('tbody').childNodes[index] || false;

                if (item.is_patient_mode && item.neqas_overdue) {
                    _.set(item, 'color', 'red');
                    currentRow.classList.add(...['colored', 'new', `color-${item.color}`]);
                }

                if (item.is_patient_mode !== undefined) {
                    if (item.is_patient_mode) return;
                    _.set(item, 'color', 'red');
                    currentRow.classList.add(...['colored', 'new', `color-${item.color}`]);
                }

                if (item.is_passed !== undefined) {
                    if (item.is_passed) return;
                    _.set(item, 'color', 'red');
                    currentRow.classList.add(...['colored', 'new', `color-${item.color}`]);
                }

                if (item.low_passed !== undefined || item.normal_passed !== undefined) {
                    if (!!parseInt(item.low_passed) && !!parseInt(item.normal_passed)) return;
                    _.set(item, 'color', 'red');
                    currentRow.classList.add(...['colored', 'new', `color-${item.color}`]);
                }

                if (!currentRow || !item || !item.status || !item.type) {
                    return;
                }

                switch (item.type) {
                    case 'blood_result':
                        _.set(item, 'color', 'red');
                        break;
                    case 'adverse_event':
                        _.set(item, 'color', 'amber');
                        break;
                    case 'record_changes':
                        _.set(item, 'color', 'blue');
                        break;
                    default:
                        _.set(item, 'color', 'blue');
                        break;
                }
                currentRow.classList.add(...['notification', item.status, `color-${item.color}`]);
            });
        },
        setPreviewMode(mode) {
            if (!mode) {
                this.resetTable();
                _.delay(
                    () => {
                        this.showFilters = true;
                    },
                    200,
                    'later'
                );
            }
            if (mode) {
                this.showFilters = false;
                this.filtersWrap = false;
                const idFilters = { id: `in:${this.selectedItems.map((x) => x.id).join(',')}` };
                this.filters = _.assign({}, this.initialFilters, this.filters, idFilters);
                this.applyFilters();
            }
        },
        openPageConfigDialog() {
            const element = this.$el.querySelector('table');
            const offset = 50;
            const headerOffset = document.querySelector('.v-app-bar').offsetHeight + offset;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition - headerOffset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
            this.$refs.filterSelector.columnsEditorDialog = true;
        },
        initialState() {
            return {
                singleSelect: false,
                selected: _.clone(this.selectedItems),
                footerProps: {
                    'items-per-page-options': [15, 30, 50, 100],
                    'items-per-page-text': t('datatable::pagination.items.per.page.text'),
                },
                items: [],
                headers: [],
                loading: false,
                totalItems: null,
                options: {
                    sortBy: [],
                    page: 1,
                },
                metaData: {},
                filterSelectorKey: 0,
                hidden: _.clone(this.hiddenColumns),
                tableColumns: _.clone(this.columns),
                filters: {},
                bulkOperations: {},
                tableParameters: {},
                windowWidth: window.innerWidth,
                selectedPeriod: null,
                selectedReportType: null,
                showFilters: _.clone(this.filtersExpanded),
                filtersWrap: _.clone(this.hasFilters),
                isPreviewMode: _.clone(this.previewMode),
                datatableInternalKey: this.datatableKey,
            };
        },
        removeEmpty(obj) {
            return Object.fromEntries(
                Object.entries(obj).filter(([k, v]) => {
                    if (k === 'is_registered') {
                        return true;
                    }
                    if ((k !== undefined && v === null) || v === undefined) return false;
                    if (typeof v === 'string' && v.trim() === '') return false;
                    if (Array.isArray(v) && v.length === 0) return false;
                    if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return false;
                    return true;
                })
            );
        },

        loadItems() {
            if (this.loading) {
                return;
            }

            this.loading = true;
            this.$el.querySelectorAll('thead.v-data-table-header').forEach((el) => {
                el.classList.add('fix');
                if (el.classList.contains('clone-header')) {
                    el.style.display = 'none';
                }
            });
            return new Promise((resolve, reject) => {
                this.filters = this.removeEmpty(this.filters);
                this.filters = _.assign({}, this.initialFilters, this.filters);
                this.$emit('update-filters', this.filters);

                this.$store.dispatch('data_table/setInitialsFilters', this.initialFilters);
                this.$store.dispatch('data_table/setFilters', this.filters);
                this.$store.dispatch('data_table/setSort', this.sort);
                this.$store.dispatch('data_table/setFields', this.fields);

                let parameters = _.assign({}, this.filters, {
                    fields: this.fields.join(','),
                    with_deleted: this.withDeleted ? 1 : 0,
                    limit: this.options && this.options.itemsPerPage ? this.options.itemsPerPage : this.perPage,
                    page: this.options.page,
                    sort: this.sort.join(','),
                    meta: this.metaFields.join(','),
                });

                parameters = { ...parameters, ...this.selectedPeriod };

                this.persistParameters();

                axios
                    .get(this.endpoint, {
                        params: parameters,
                    })
                    .then((response) => {
                        if (!response) {
                            return;
                        }

                        let columns = this.tableColumns;

                        this.$store.dispatch('data_table/setColumns', columns);
                        this.$store.dispatch('data_table/setHiddenColumns', this.hidden);

                        let items = _.map(response.data.data, (row) => {
                            return row;
                        });

                        this.$store.dispatch('data_table/setItems', items);

                        let total = response.data.meta.pagination.total_items;

                        this.metaData = response.data.meta;
                        this.$store.dispatch('data_table/setMeta', this.metaData);

                        delete this.metaData.pagination;

                        resolve({
                            columns,
                            items,
                            total,
                        });
                    })
                    .catch((error) => {
                        reject(error);
                    })
                    .then(() => {
                        this.notification ? this.notificationMode() : false;
                        this.$el.querySelectorAll('thead.v-data-table-header').forEach((el) => {
                            if (el.classList.contains('clone-header')) {
                                el.style.display = 'table-header-group';
                            }
                            el.classList.remove('fix');
                        });
                        this.loading = false;
                    });
            });
        },
        updateColumns(columns, type) {
            if (!columns) {
                return false;
            }
            switch (type) {
                case 'hidden':
                    return this.updateHiddenColumns(columns);
                case 'order':
                    return this.updateOrderColumns(columns);
                default:
                    return false;
            }
        },
        updateOrderColumns: _.debounce(function (columns) {
            this.headers = columns;
            this.persistParameters();
        }, 100),
        updateHiddenColumns: _.debounce(function (hidden) {
            this.hidden = hidden;
            this.persistParameters();
            this.datatableInternalKey++;
        }, 100),
        createFilter(filterName) {
            let endPoint =
                this.endpoint && this.endpoint.template
                    ? this.endpoint.template.replace(/\/(\{.*?\})/, '')
                    : this.endpoint;
            if (this.currentTab !== null) {
                endPoint = `${endPoint}/${this.currentTab}`;
            }
            const dataTableState = {
                headers: this.$store.state.data_table.columns,
                hidden: this.$store.state.data_table.hiddenColumns,
                isPreviewMode: this.tableParameters.isPreviewMode,
                options: this.tableParameters.options,
                selected: this.tableParameters.selected,
                selectedPeriod: this.tableParameters.selectedPeriod,
                selectedReportType: this.tableParameters.selectedReportType,
                showFilters: this.tableParameters.showFilters,
                filters: this.tableParameters.filters,
            };
            const data = {
                data: dataTableState,
                name: filterName,
                route: endPoint,
            };

            this.$store
                .dispatch('data_table/saveScreenConfiguration', data)
                .then(() => {
                    this.$root.notify('info', `${t('filters::notification.filter.saved')} ${data.name}`);
                    this.filterSelectorKey++;
                    this.$refs.filterSelector.saveDialog = false;
                })
                .catch((error) => {
                    if (!this.$refs.filterSelector) {
                        return;
                    }
                    if (error.response && error.response.status === 400) {
                        this.$refs.filterSelector.errorMessages.name = [];
                        this.$refs.filterSelector.errorMessages.name.push(
                            t('filters::validation.filter.name.required')
                        );
                    } else if (!error.success) {
                        this.$refs.filterSelector.errorMessages.name = [];
                        this.$refs.filterSelector.errorMessages.name.push(error.response.data.error);
                    } else {
                        this.$root.notify('error', t('default::notification.errors.general'));
                        this.$refs.filterSelector.saveDialog = false;
                    }
                });
        },
        setFilter(filter) {
            if (!filter && !filter.data) {
                this.resetTable();
            }
            Object.assign({}, this.$data, filter.data);
            if (filter.data && filter.data && filter.data.headers && filter.data.hidden) {
                this.headers = filter.data.headers;
                this.hidden = filter.data.hidden;
                this.filters = filter.data.filters;
                this.$store.dispatch('data_table/setColumns', filter.data.headers);
                this.$store.dispatch('data_table/setHiddenColumns', filter.data.hidden);
                _.delay(() => {
                    return this.applyFilters(false, false);
                }, 100);
            }
        },
        applyFilters(scroll = false, setHeaders = true) {
            this.selected = [];
            this.options.page = 1;
            let loadedItems = this.loadItems();
            if (loadedItems) {
                loadedItems.then((data) => {
                    if (setHeaders) {
                        this.headers = data.columns;
                    }
                    this.items = data.items;
                    this.totalItems = data.total;
                    if (scroll) {
                        this.scrollToTable();
                    }
                });
            }
        },
        scrollToTable() {
            const element = this.$el.querySelector('table');
            const offset = 10;
            const headerOffset = document.querySelector('.v-app-bar').offsetHeight + offset;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition - headerOffset;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        },
        resetTable() {
            this.$emit('onReset');
            let defaultState = this.initialState();
            defaultState.showFilters = this.showFilters;
            defaultState.tableColumns.map((x) => {
                x.isVisible = defaultState.hidden.includes(x.value) ? false : true;
            });

            if (this.selectedPeriod) {
                defaultState.filters = Object.assign(this.initialFilters, this.selectedPeriod);
            }
            if (!this.selectedPeriod) {
                defaultState.filters = this.initialFilters;
            }

            // this.options.page = 1;

            _.delay(() => {
                defaultState.options.page = 1;
                Object.assign(this.$data, defaultState);
                this.$store.dispatch('data_table/setColumns', defaultState.headers || []);
                this.$store.dispatch('data_table/setHiddenColumns', defaultState.hidden || []);
                this.$store.dispatch('data_table/setFilters', defaultState.filters || {});
                this.$store.dispatch('data_table/setSort', defaultState.sort || []);
                this.$store.dispatch('data_table/resetScreenConfiguration');
            }, 100);
        },
        persistParameters() {
            let tableParameters = _.clone(this.$data);
            // _.unset(tableParameters, 'tableParameters');
            // _.unset(tableParameters, 'items');
            // _.unset(tableParameters, 'totalItems');
            // _.unset(tableParameters, 'loading');

            tableParameters.tableParameters = {};
            tableParameters.items = [];
            tableParameters.totalItems = null;
            tableParameters.loading = false;
            tableParameters.showFilters = this.showFilters;
            tableParameters.headers = this.headers;
            if (this.keepFilters) {
                //Allow to keep in storage if report_type is not set or if it is set it is equal to keepFiltersPrefix
                if (
                    !Object.prototype.hasOwnProperty.call(tableParameters.filters, 'report_type') ||
                    tableParameters.filters?.report_type === this.keepFiltersPrefix
                ) {
                    let key = this.keepFiltersPrefix + '-' + this.endpoint;
                    window.localStorage.setItem(key, JSON.stringify(tableParameters));
                }
            }
            this.tableParameters = tableParameters;
        },
        onResize() {
            this.windowWidth = window.innerWidth;
        },
        onScroll() {
            const element = this.$el.querySelector('table');
            const offset = 10;
            const headerOffsetNatural = document.querySelector('.v-app-bar').offsetHeight;
            const headerOffset = headerOffsetNatural + offset;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition - headerOffset;
            let tableHeader = this.$el.querySelector('thead.v-data-table-header');
            const tableHeaders = Array.from(this.$el.querySelectorAll('thead.v-data-table-header'));
            const cloneHeader = tableHeader.cloneNode(true);
            const tableHeaderVisibleWidth = this.$el.querySelector('.v-data-table__wrapper')
                ? this.$el.querySelector('.v-data-table__wrapper').clientWidth
                : false;

            if (tableHeaders.length === 1) {
                element.appendChild(cloneHeader);
            }
            const fixedHeader = tableHeaders[0];
            const fakeHeader = tableHeaders[1];
            if (!fixedHeader) {
                return;
            }
            fakeHeader ? fakeHeader.classList.add('clone-header') : false;

            let screenConfig = document.querySelectorAll('.v-data-table-header-column');

            const tableFirstRow = this.$el.querySelectorAll('table tbody tr')[2];

            if (offsetPosition <= offset) {
                fixedHeader.classList.contains('fixed-header') ? false : fixedHeader.classList.add('fixed-header');
                fixedHeader.style.top === `${headerOffsetNatural}px`
                    ? false
                    : (fixedHeader.style.top = `${headerOffsetNatural}px`);
                fixedHeader.style.width === `${tableHeaderVisibleWidth}px`
                    ? false
                    : (fixedHeader.style.width = `${tableHeaderVisibleWidth}px`);
                const tableScrolled = this.$el.querySelector('.v-data-table__wrapper');
                if (tableScrolled) {
                    fixedHeader.scrollLeft = tableScrolled.scrollLeft;
                }
                if (tableFirstRow && tableFirstRow.childNodes) {
                    tableFirstRow.childNodes.forEach((child, i) => {
                        const headerTh = fixedHeader.querySelector('tr').childNodes[i];
                        if (headerTh.offsetWidth !== child.offsetWidth) {
                            headerTh.style.minWidth = `${child.offsetWidth}px`;
                        }
                    });
                }

                if (screenConfig) {
                    screenConfig.forEach((el) => {
                        el.style.position = 'fixed';
                        el.style.top === `${headerOffsetNatural}px`
                            ? false
                            : (el.style.top = `${headerOffsetNatural}px`);
                        el.style.right === '25px' ? false : (el.style.right = '25px');
                        el.style.zIndex === 6 ? false : (el.style.zIndex = 6);
                    });
                }

                // this.$el.querySelector('.v-data-table__wrapper') &&
                // this.$el.querySelector('.v-data-table__wrapper').style.overflowX !== 'hidden'
                //     ? (this.$el.querySelector('.v-data-table__wrapper').style.overflowX = 'hidden')
                //     : false;
            } else {
                fixedHeader.classList.contains('fixed-header') ? fixedHeader.classList.remove('fixed-header') : false;
                fixedHeader.style.width = 'unset';
                fixedHeader.style.top = 'unset';
                Array.from(this.$el.querySelectorAll('thead.v-data-table-header')).forEach((el, i) => {
                    if (i !== 0) {
                        el.remove();
                    }
                });
                // this.$el.querySelector('.v-data-table__wrapper') &&
                // this.$el.querySelector('.v-data-table__wrapper').style.overflowX !== 'scroll'
                //     ? (this.$el.querySelector('.v-data-table__wrapper').style.overflowX = 'scroll')
                //     : false;
            }
        },
        onTableScroll() {
            const element = this.$el.querySelector('.v-data-table__wrapper');
            const header = this.$el.querySelector('.fixed-header');
            const fakeScroller = this.$el.querySelector('.v-data-table-fake-header');
            if (!element && !header) {
                return;
            }
            if (header) {
                header.onscroll = (e) => {
                    element ? (element.scrollLeft = e.target.scrollLeft) : false;
                };
            }
            if (fakeScroller) {
                fakeScroller.onscroll = (e) => {
                    element ? (element.scrollLeft = e.target.scrollLeft) : false;
                };
            }
            element.onscroll = (e) => {
                header ? (header.scrollLeft = e.target.scrollLeft) : false;
                fakeScroller ? (fakeScroller.scrollLeft = e.target.scrollLeft) : false;
            };
        },
        search(event) {
            const doSearch = () => {
                event.preventDefault();
                event.stopPropagation();
                const hasFieldFilters = Object.values(this.filters).filter((x) => x);
                return hasFieldFilters && hasFieldFilters.length ? this.applyFilters(true) : false;
            };
            if (event.key === 'Enter' && event.target && event.target.tagName === 'INPUT') {
                doSearch();
            }
        },
    },
};
</script>
<style>
.v-data-table td,
.v-data-table th {
    padding-left: 8px;
}
</style>
